/**
 * Floating-element overlay (F-30/F-32, docs/04 §5): DOM nodes positioned in
 * board space inside the SAME transformed camera layer as the table — the
 * shared CSS `translate(...) scale(zoom)` is what keeps layers aligned, so
 * elements render in raw BOARD units. `zoom` is used only to convert pointer
 * deltas to board units and to counter-scale the interaction handles.
 *
 * Interactions:
 *  - click selects; Cmd/Ctrl/Shift+click toggles it in and out of a
 *    multi-selection (F-34); drag moves — the whole selection when the dragged
 *    element is part of one; corner handles resize (aspect-locked for images,
 *    Shift for free) — previewed locally at pointer rate, streamed to peers via
 *    awareness (docs/07 C9), and committed as ONE accessor write of ONLY the
 *    changed geometry keys on release (same one-commit pattern as F-14 column
 *    resize; per-property keys, C2).
 *  - DOUBLE-click — double-TAP on touch — opens a link card's URL in the OS
 *    default browser (F-31, amended 2026-08-30). A single click only selects,
 *    which is what makes a card selectable at all; where the URL goes, and
 *    which URLs are allowed to, is registries/element-types/linkCard/open.ts.
 *  - edge ports (F-38): bringing the cursor near an edge arms that side's
 *    connection point; dragging out of it draws a connector, finished by the
 *    ConnectorsLayer (a drag ends anywhere on the canvas, not just on an
 *    element). Pen strokes have no ports — see `canConnectTo`.
 *  - context menu: add comment / copy link / download (an image or a file
 *    element — F-135, the same action as the toolbar's button) / bring to
 *    front / send to back / delete; Del key deletes (handled by the canvas
 *    shell, which owns selection).
 *  - F-46 grab indicator: while dragging we claim the element via awareness;
 *    remote claims tint the element in the grabber's color and suppress our
 *    own drag (docs/07 C9 soft lock — awareness only, never server state).
 */
import type { ConnectorSide, ElementGeometryPatch, ElementInfo } from "@kola/shared";
import { CONNECTOR_SIDES } from "@kola/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";

import {
  COARSE_POINTER,
  DOUBLE_TAP_MS,
  LONG_PRESS_MS,
  safeSetPointerCapture,
  type CanvasStore,
} from "../../core/canvas.js";
import { withAlpha } from "../../core/color.js";
import { rectIntersectsCull } from "../../core/row-window.js";
import type { BoardSession, ElementMovePlan, RemotePeer } from "../../core/session.js";
import { useCullRect } from "../../core/useCullRect.js";
import { elementRenderers } from "../../registries/element-types/index.js";
import { downloadElementAsset, downloadTarget } from "../../registries/element-types/file/download.js";
import { linkCardUrl, openLinkInBrowser } from "../../registries/element-types/linkCard/open.js";
import { requestAddComment, type BoardRect } from "../../registries/toolbar-actions/index.js";
import { copyToClipboard, elementShareLink } from "../../core/share-link.js";
import { ContextMenu, type MenuState } from "../board/ContextMenu.js";
import { showToast } from "../../ui/toast.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { canConnectTo, nearestSide, portPoint } from "../connectors/geometry.js";
import { PORT_HOVER_SCREEN_PX, type ConnectorUiStore } from "../connectors/store.js";
import {
  anchorsEqual,
  containedCellGone,
  containedElementRect,
  elementConfinesToCell,
  reoffsetWithinCell,
  resolveElementRect,
} from "./contained.js";
import { offsetRect, planElementMove } from "./move-plan.js";
import type { ElementsUiStore } from "./store.js";

const MIN_ELEMENT_BOARD_PX = 16;

/** Corner-handle size (screen px) — bigger where fingers do the resizing. */
const HANDLE_SCREEN_PX = COARSE_POINTER ? 18 : 10;
const HANDLE_OFFSET_SCREEN_PX = HANDLE_SCREEN_PX / 2 + 1;

/** F-38 port dot size (screen px) — same "fingers need more" rule as handles. */
const PORT_SCREEN_PX = COARSE_POINTER ? 16 : 9;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type Corner = "nw" | "ne" | "sw" | "se";
const CORNERS: readonly Corner[] = ["nw", "ne", "sw", "se"];

interface DragState {
  pointerId: number;
  mode: "move" | Corner;
  startClientX: number;
  startClientY: number;
  orig: Rect;
  /**
   * Where the cursor sat INSIDE the element when the press landed, in board
   * units. Constant for the whole gesture (element and cursor travel by the
   * same delta), so `finalRect.topLeft + grab` is the drop point — the point
   * that decides which cell now owns the element (F-36 `reanchorForDrop`).
   */
  grab: { dx: number; dy: number };
  moved: boolean;
  /**
   * F-34: the OTHER selected elements riding this move, with the rect each had
   * when the drag began. Captured once at pointerdown — re-resolving them per
   * frame would let a concurrent remote edit shear the group apart mid-gesture.
   * Empty for a resize and for a drag on an element that is not part of a
   * multi-selection.
   */
  group: Array<{ id: string; orig: Rect }>;
}

function resizeRect(orig: Rect, corner: Corner, dx: number, dy: number, lockAspect: boolean): Rect {
  let w = corner.includes("e") ? orig.w + dx : orig.w - dx;
  let h = corner.includes("s") ? orig.h + dy : orig.h - dy;
  w = Math.max(MIN_ELEMENT_BOARD_PX, w);
  h = Math.max(MIN_ELEMENT_BOARD_PX, h);
  if (lockAspect && orig.w > 0 && orig.h > 0) {
    // Follow whichever axis changed more, keep the original aspect.
    if (Math.abs(w / orig.w - 1) >= Math.abs(h / orig.h - 1)) {
      h = Math.max(MIN_ELEMENT_BOARD_PX, (w * orig.h) / orig.w);
    } else {
      w = Math.max(MIN_ELEMENT_BOARD_PX, (h * orig.w) / orig.h);
    }
  }
  return {
    x: corner.includes("w") ? orig.x + orig.w - w : orig.x,
    y: corner.includes("n") ? orig.y + orig.h - h : orig.y,
    w,
    h,
  };
}

export interface ElementsLayerProps {
  session: BoardSession;
  canvas: CanvasStore;
  /**
   * F-36: the grid's published layout pass — cell-anchored (contained)
   * elements derive their absolute board position from the cell rect it
   * carries + their `(dx, dy)` offset. Null when the grid isn't mounted yet;
   * contained elements then fall back to their `x`/`y` cache.
   */
  layout: GridLayoutSnapshot | null;
  /** F-34: the whole element selection (empty, one, or many). */
  selectedIds: readonly string[];
  /**
   * `additive` = a Cmd/Ctrl/Shift click: toggle this element in the selection
   * instead of replacing it. A null id always clears.
   */
  onSelect: (elementId: string | null, additive: boolean) => void;
  /** Live drag rects, shared with the connector layer (see ./store). */
  elementsUi: ElementsUiStore;
  /** F-38 connector drafting + port arming. */
  connectorUi: ConnectorUiStore;
  /**
   * F-27: fires true once a move/resize drag actually moves (a plain click
   * never fires it) and false when it ends — the selection toolbar hides
   * while an element drag is in flight.
   */
  onDragActive?: (active: boolean) => void;
}

export function ElementsLayer({
  session,
  canvas,
  layout,
  selectedIds,
  onSelect,
  elementsUi,
  connectorUi,
  onDragActive,
}: ElementsLayerProps) {
  const zoom = useStore(canvas, (s) => s.camera.zoom);
  const cull = useCullRect(canvas);
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);
  const frozen = useStore(session.store, (s) => s.frozen);
  const synced = useStore(session.store, (s) => s.synced);
  const peers = useStore(session.store, (s) => s.peers);
  const [menu, setMenu] = useState<MenuState | null>(null);

  // Paint order = (z, id) — replica-identical stacking (shared accessor).
  const elements = useMemo(() => {
    void elementsVersion;
    return session.board.listElements();
  }, [session, elementsVersion]);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  /** elementId → the peer currently grabbing it (F-46). */
  const remoteGrabs = useMemo(() => {
    const map = new Map<string, RemotePeer>();
    for (const peer of peers) {
      if (peer.grabbedElement) map.set(peer.grabbedElement.elementId, peer);
    }
    return map;
  }, [peers]);

  // C4/F-36 contained-element degradation (docs/07 C4, docs/04 §2), the exact
  // mirror of the comment-pin rule (CommentPinsLayer): a contained element
  // whose anchor cell (row/column) is deleted degrades to a BOARD anchor at its
  // LAST RESOLVED position, so it never lingers as a zombie pinned to a dead
  // cell (rendering from a stale x/y cache — the origin for a migrated image).
  //
  // Last successfully-resolved board rect per element = the "last resolved
  // position" to degrade to. Recorded in an effect (declared BEFORE the
  // degradation effect) that only writes when the cell STILL resolves, so on
  // the render where the cell just vanished the map keeps the previous pass's
  // position.
  const lastResolvedRef = useRef(new Map<string, BoardRect>());
  useEffect(() => {
    for (const el of elements) {
      if (el.anchor.kind !== "cell") continue;
      const rect = containedElementRect(
        el.anchor,
        el.w,
        el.h,
        layout,
        elementConfinesToCell(el.type),
      );
      if (rect !== null) lastResolvedRef.current.set(el.id, rect);
    }
  }, [elements, layout]);

  // Gated on `synced` (an un-synced doc proves nothing about existence) and the
  // F-45 freeze (no doc writes while frozen — another client, or this one after
  // reconnect, degrades instead). Idempotent + LWW across every client racing
  // the same deletion (degradeElementToBoard re-checks in the transaction).
  useEffect(() => {
    if (!synced || frozen) return;
    for (const el of elements) {
      if (el.anchor.kind !== "cell") continue;
      if (!containedCellGone(el.anchor, layout)) continue;
      const last = lastResolvedRef.current.get(el.id);
      if (last !== undefined) session.degradeElementToBoard(el.id, last);
    }
  }, [session, elements, layout, synced, frozen]);

  const openMenu = useCallback(
    (element: ElementInfo, x: number, y: number) => {
      // A right-click inside a multi-selection acts on the WHOLE selection —
      // the menu the user opened is the one over the group they can see
      // outlined, so "Delete" there must not silently drop just one of them.
      const inGroup = selected.has(element.id) && selected.size > 1;
      const targets = inGroup ? [...selected] : [element.id];
      setMenu({
        x,
        y,
        items: [
          {
            // F-50 second entry path: right-click any element → Add comment.
            // Routed through the same registry hook as the F-27 toolbar
            // button; geometry re-resolved at CLICK time (the menu can sit
            // open across remote moves/deletes — C4-style id re-check).
            label: "Add comment",
            onClick: () => {
              const current = session.board.getElement(element.id);
              if (current === undefined) return; // deleted while the menu sat open
              // Resolve the element's CURRENT board rect (a contained element's
              // real position comes from its cell, not the x/y cache) so the
              // pin lands on it.
              requestAddComment(session, {
                kind: "element",
                elementId: current.id,
                rect: resolveElementRect(current, layout),
              });
            },
          },
          {
            // F-123: a link that opens the board framed on this element.
            label: "Copy link",
            title: "A link that opens this board on this item",
            onClick: () => {
              void copyToClipboard(elementShareLink(session.boardId, element.id)).then((copied) =>
                showToast(copied ? "Link to this item copied" : "Couldn't reach the clipboard"),
              );
            },
          },
          // F-135: save this element's file — an image or a file element;
          // omitted, not greyed, for a link card or a stroke (nothing to
          // save). Re-resolved at click time like the comment above: the
          // element the menu was opened on may be gone.
          ...(downloadTarget(element) !== null
            ? [
                {
                  label: "Download",
                  title: "Save this file to your computer",
                  onClick: () => {
                    const current = session.board.getElement(element.id);
                    if (current !== undefined) void downloadElementAsset(current);
                  },
                },
              ]
            : []),
          {
            label: "Bring to front",
            separatorBefore: true,
            onClick: () => {
              for (const id of targets) session.bringElementToFront(id);
            },
          },
          {
            label: "Send to back",
            onClick: () => {
              for (const id of targets) session.sendElementToBack(id);
            },
          },
          {
            label: inGroup ? `Delete ${targets.length} items` : "Delete",
            danger: true,
            separatorBefore: true,
            onClick: () => {
              session.deleteElements(targets);
              onSelect(null, false);
            },
          },
        ],
      });
    },
    [session, onSelect, layout, selected],
  );

  // F-10 viewport culling: mount only the elements near the screen. With the
  // table windowed, a contained element still RESOLVES anywhere (the grid
  // publishes edges for every row — that is what makes jump-to-pin work at any
  // distance), so nothing else would stop a board's whole element set from
  // mounting, and loading its images, while off-screen.
  //
  // Two kinds of element are NEVER culled, because unmounting an ElementView
  // mid-drag kills its pointer capture and its cleanup fires
  // `setGrabbedElement(undefined)` — the drag would silently die and the F-46
  // claim would flicker:
  //   · a SELECTED one — `beginDrag` selects before it drags, so this covers
  //     our own drag (and keeps a selected element's handles alive while the
  //     user pans away from it), and
  //   · one a peer is grabbing — its rect is their awareness-streamed preview,
  //     which is exactly the motion we must not drop frames of (C9).
  // Culling is by the element's RESOLVED rect, so a contained element is
  // judged by where its cell actually is, not by its x/y fallback cache.
  const visible = elements.filter(
    (element) =>
      selected.has(element.id) ||
      remoteGrabs.has(element.id) ||
      rectIntersectsCull(resolveElementRect(element, layout), cull),
  );

  return (
    <div className="elements-layer">
      {visible.map((element) => (
        <ElementView
          key={element.id}
          session={session}
          element={element}
          layout={layout}
          zoom={zoom}
          frozen={frozen}
          selected={selected.has(element.id)}
          selectedIds={selectedIds}
          remoteGrab={remoteGrabs.get(element.id)}
          onSelect={onSelect}
          onOpenMenu={openMenu}
          onDragActive={onDragActive}
          elementsUi={elementsUi}
          connectorUi={connectorUi}
        />
      ))}
      {menu !== null && <ContextMenu menu={menu} onClose={() => setMenu(null)} />}
    </div>
  );
}

interface ElementViewProps {
  session: BoardSession;
  element: ElementInfo;
  layout: GridLayoutSnapshot | null;
  zoom: number;
  frozen: boolean;
  selected: boolean;
  selectedIds: readonly string[];
  remoteGrab: RemotePeer | undefined;
  onSelect: (elementId: string | null, additive: boolean) => void;
  onOpenMenu: (element: ElementInfo, x: number, y: number) => void;
  onDragActive?: ((active: boolean) => void) | undefined;
  elementsUi: ElementsUiStore;
  connectorUi: ConnectorUiStore;
}

function ElementView({
  session,
  element,
  layout,
  zoom,
  frozen,
  selected,
  selectedIds,
  remoteGrab,
  onSelect,
  onOpenMenu,
  onDragActive,
  elementsUi,
  connectorUi,
}: ElementViewProps) {
  /** Live-local preview rect during a drag/resize (board units). */
  const [localRect, setLocalRect] = useState<Rect | null>(null);
  /** Same value in a ref — the commit path must not run inside a state updater. */
  const localRectRef = useRef<Rect | null>(null);
  const dragRef = useRef<DragState | null>(null);
  /**
   * F-31 click-vs-drag guard for the link-card open action: set true on a left
   * pointerdown that STARTS on a card body, and cleared the moment the gesture
   * becomes a drag — so the double-click that opens the URL is only ever the
   * one whose presses stayed put (a nudge-then-click never opens anything).
   */
  const openIntentRef = useRef(false);
  /**
   * Touch: timestamp of the previous clean tap on THIS card (the finger's
   * stand-in for a double-click, same window as the grid's double-tap-to-edit),
   * or null. A tap fires no compatibility click/dblclick at all — beginDrag
   * preventDefaults the pointerdown — so the pair is counted here. The ref
   * belongs to one element's view, which is what makes "both taps on the same
   * card" true without tracking a target.
   */
  const lastTapRef = useRef<number | null>(null);
  /** Touch: pending long-press timer (context menu — right-click stand-in). */
  const longPressRef = useRef<number | null>(null);

  /**
   * F-34: this element's share of a group drag. While WE are the element under
   * the pointer our own `localRect` outranks it (see `rect` below), so one
   * subscription serves both roles.
   */
  const followerRect = useStore(elementsUi, (s) => s.live.get(element.id));
  /** F-38: the port armed under the cursor, when it is one of ours. */
  const armedSide = useStore(connectorUi, (s) =>
    s.hover?.elementId === element.id ? s.hover.side : null,
  );
  /** A connector drag is in flight somewhere — every element offers its ports. */
  const draftActive = useStore(connectorUi, (s) => s.draft !== null);
  /** …and this is the port it would land on right now. */
  const snapSide = useStore(connectorUi, (s) => {
    const snap = s.draft?.snap;
    return snap !== undefined && snap !== null && snap.elementId === element.id ? snap.side : null;
  });

  const cancelLongPress = () => {
    if (longPressRef.current !== null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  /** Abandon an in-flight drag with NO commit — the pointer was taken over
   * (two-finger camera gesture stole it via setPointerCapture, delivering
   * `lostpointercapture` here) or a long-press turned the press into a menu.
   * The local preview is discarded, so geometry reverts to the doc's. */
  const cancelDrag = () => {
    cancelLongPress();
    const drag = dragRef.current;
    if (drag === null) return;
    dragRef.current = null;
    session.setGrabbedElement(undefined);
    elementsUi.getState().clearLive();
    if (drag.moved) onDragActive?.(false);
    openIntentRef.current = false;
    updateLocalRect(null);
  };

  const onLostPointerCapture = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    cancelDrag();
  };

  const updateLocalRect = (next: Rect | null) => {
    localRectRef.current = next;
    setLocalRect(next);
  };

  // The element's absolute board rect for THIS pass: a cell-anchored element
  // derives it from the grid layout (cell rect + dx/dy); a board-anchored one
  // uses its x/y cache (F-36).
  const baseRect = resolveElementRect(element, layout);

  // A CONTAINED element whose cell can't be resolved this pass — grid not
  // mounted yet, a freshly-inserted row whose edge isn't measured, or its
  // row/column just deleted and not yet degraded (ElementsLayer's C4 effect) —
  // must NOT paint at its stale x/y cache: for a migrated image that cache is
  // (0,0), i.e. the origin flash. Hide it until its cell resolves or the
  // degradation re-homes it to a board anchor. Never hide while a live
  // preview (our drag) or a remote grab rect is driving the render.
  const cellUnresolvable =
    element.anchor.kind === "cell" &&
    containedElementRect(
      element.anchor,
      element.w,
      element.h,
      layout,
      elementConfinesToCell(element.type),
    ) === null;

  // Render precedence: our own live preview, then this element's share of a
  // group drag someone else in the selection is leading, then a remote
  // grabber's awareness-streamed rect (C9 — B watches A's drag live), then the
  // derived board rect.
  const rect = localRect ?? followerRect ?? remoteGrab?.grabbedElement?.rect ?? baseRect;
  const canInteract = !frozen && remoteGrab === undefined;
  /** F-38: everything but a pen stroke offers connection ports. */
  const canConnect = canInteract && canConnectTo(element.type);

  /** Cursor position inside the pressed element, in board units. */
  const grabOffset = (event: React.PointerEvent, z: number) => {
    const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
    return { dx: (event.clientX - box.left) / z, dy: (event.clientY - box.top) / z };
  };

  const beginDrag = (event: React.PointerEvent, mode: DragState["mode"]) => {
    if (!canInteract || event.button !== 0) return;
    event.stopPropagation(); // never starts a canvas pan
    event.preventDefault();
    // A press on an element ALREADY in a multi-selection keeps that selection,
    // so the whole group drags. Anything else selects just this element.
    if (!selected) onSelect(element.id, false);
    const group: DragState["group"] = [];
    if (mode === "move" && selected && selectedIds.length > 1) {
      for (const id of selectedIds) {
        if (id === element.id) continue;
        const other = session.board.getElement(id);
        if (other === undefined) continue; // deleted between selection and press
        group.push({ id, orig: resolveElementRect(other, layout) });
      }
    }
    dragRef.current = {
      pointerId: event.pointerId,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      // Absolute resolved rect — a contained element drags in board space too;
      // its offset is recomputed against the cell under it on release.
      orig: baseRect,
      // Measured against the wrapper's own screen box, which IS `baseRect`
      // under the camera transform — no container rect needed.
      grab: grabOffset(event, zoom),
      moved: false,
      group,
    };
    safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
    // F-46: claim the element while we manipulate it (awareness only). A group
    // drag claims only the element under the pointer — awareness carries one
    // grab per client, and the followers' motion reaches peers on release.
    session.setGrabbedElement({ elementId: element.id });
    if (event.pointerType === "touch" && mode === "move") {
      // Mobile: holding an element still opens its context menu (right-click
      // stand-in). Any real movement below re-arms it as a plain drag.
      cancelLongPress();
      const { clientX, clientY } = event;
      longPressRef.current = window.setTimeout(() => {
        longPressRef.current = null;
        const drag = dragRef.current;
        if (drag === null || drag.moved) return;
        cancelDrag(); // nothing moved — this releases the grab claim only
        if (!frozen) onOpenMenu(element, clientX, clientY);
      }, LONG_PRESS_MS);
    }
  };

  /**
   * Wrapper-only pointer move: a drag frame, or — when nothing is being
   * dragged — the F-38 port arming. The corner handles use `onPointerMove`
   * directly, because `armPort` measures against the event's currentTarget and
   * a handle's own rect is not the element's.
   */
  const onWrapperPointerMove = (event: React.PointerEvent) => {
    if (dragRef.current === null) {
      armPort(event);
      return;
    }
    onPointerMove(event);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    const dx = (event.clientX - drag.startClientX) / zoom;
    const dy = (event.clientY - drag.startClientY) / zoom;
    if (!drag.moved && Math.abs(dx) * zoom < 2 && Math.abs(dy) * zoom < 2) return;
    if (!drag.moved) {
      onDragActive?.(true); // fires ONCE, on the first real move
      cancelLongPress(); // a moved gesture is a drag, never a long-press menu
      connectorUi.getState().setHover(null); // no port affordance mid-drag
    }
    drag.moved = true;
    openIntentRef.current = false; // a moved gesture is a drag, never a link-open click
    const next =
      drag.mode === "move"
        ? { ...drag.orig, x: drag.orig.x + dx, y: drag.orig.y + dy }
        : // Aspect-locked by default (images); Shift = free (F-32).
          resizeRect(drag.orig, drag.mode, dx, dy, !event.shiftKey);
    updateLocalRect(next);
    // Publish every rect this gesture is moving: the followers of a group drag
    // render from it, and the F-38 connector layer rides it so a line tracks
    // the motion on THIS client too (awareness never echoes our own grab back).
    const live = new Map<string, BoardRect>([[element.id, next]]);
    for (const follower of drag.group) live.set(follower.id, offsetRect(follower.orig, dx, dy));
    elementsUi.getState().setLive(live);
    // C9: stream the live rect via awareness so remote users see the motion
    // (not a frozen element that teleports on release). Ephemeral only — the
    // CRDT commit still happens ONCE in endDrag.
    session.setGrabbedElement({ elementId: element.id, rect: next });
  };

  const endDrag = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    cancelLongPress();
    dragRef.current = null;
    session.setGrabbedElement(undefined);
    elementsUi.getState().clearLive();
    if (drag.moved) onDragActive?.(false);
    if (!drag.moved && event.pointerType === "touch" && event.type === "pointerup") {
      // A touch tap produces no compatibility click/dblclick (beginDrag
      // preventDefaults the pointerdown), so the double-TAP that stands in for
      // the double-click is counted here instead.
      const now = performance.now();
      const last = lastTapRef.current;
      lastTapRef.current = now;
      if (last !== null && now - last < DOUBLE_TAP_MS) {
        lastTapRef.current = null; // a triple-tap is not two double-taps
        openLink();
      }
    }
    const finalRect = localRectRef.current;
    if (drag.moved && finalRect !== null) {
      if (drag.mode === "move") {
        // A MOVE resets the element's belonging from where it was dropped,
        // whatever it was anchored to before — into the cell under the cursor,
        // or out to the board once it is clear of the table (F-32/F-36) — for
        // EVERY element the gesture carried, in one transaction.
        commitMove(drag, finalRect);
      } else if (element.anchor.kind === "cell") {
        // A resize (any corner handle) RE-FITS the containing cell and keeps
        // it; only a MOVE may re-home the element across a cell boundary (F-32).
        commitContainedResize(drag.orig, finalRect);
      } else {
        commitBoardDrag(drag.orig, finalRect);
      }
    }
    updateLocalRect(null);
  };

  /**
   * Board-anchored RESIZE commit (F-32): ONE write of ONLY the keys this drag
   * changed (C2: x/y/w/h are independent LWW keys — re-writing an unchanged
   * x/y here, e.g. on a SE-corner resize, would revert a concurrent remote
   * move). The unchanged-detection compares exactly: resizeRect copies `orig`
   * values verbatim for the axes the gesture doesn't touch. A resize never
   * re-homes (the anchor stays board), so the anchor key is untouched.
   */
  const commitBoardDrag = (orig: Rect, finalRect: Rect) => {
    const changes: ElementGeometryPatch = {};
    if (finalRect.x !== orig.x) changes.x = Math.round(finalRect.x);
    if (finalRect.y !== orig.y) changes.y = Math.round(finalRect.y);
    if (finalRect.w !== orig.w) changes.w = Math.round(finalRect.w);
    if (finalRect.h !== orig.h) changes.h = Math.round(finalRect.h);
    if (Object.keys(changes).length > 0) session.setElementRect(element.id, changes);
  };

  /**
   * MOVE commit for ANY anchor kind and for one OR many elements (F-32/F-36/
   * F-34). Each element's belonging is RESET from where IT was dropped — the
   * plan is computed per element by `planElementMove`, so a group drag can land
   * one element in a cell and another on the board, exactly as if each had been
   * dragged separately. Every plan applies inside ONE transaction (one
   * undo step / sync unit), with the F-26/F-36 cell fits last.
   *
   * A follower deleted mid-drag (C4) is simply skipped — it has no home left to
   * reset.
   */
  const commitMove = (drag: DragState, finalRect: Rect) => {
    const dx = finalRect.x - drag.orig.x;
    const dy = finalRect.y - drag.orig.y;
    // The drop point: only the element under the pointer has one, so the
    // followers below pass none and home by their own top-left.
    const pointer = { x: finalRect.x + drag.grab.dx, y: finalRect.y + drag.grab.dy };
    const plans: ElementMovePlan[] = [
      planElementMove(element, layout, drag.orig, finalRect, pointer),
    ];
    for (const follower of drag.group) {
      const current = session.board.getElement(follower.id);
      if (current === undefined) continue;
      plans.push(
        planElementMove(current, layout, follower.orig, offsetRect(follower.orig, dx, dy)),
      );
    }
    session.applyElementMoves(plans);
  };

  /**
   * Contained (cell-anchored) RESIZE commit (F-32/F-36): KEEP the containing
   * cell and RE-FIT it; an N/W/S corner moves the top-left, but the element
   * must not re-home to a neighbour — only its offset within the SAME cell
   * changes (`reoffsetWithinCell`). The cell's board top-left is the element's
   * rect-at-drag-start (`orig`) minus its old dx/dy.
   *
   * The whole-object anchor and any size change commit in ONE transaction =
   * one undo step, then the cell READJUSTS to the element's new extent
   * (`fitCell`): grows for a larger element, shrinks back for a smaller one.
   */
  const commitContainedResize = (orig: Rect, finalRect: Rect) => {
    const current = element.anchor;
    if (current.kind !== "cell") return; // caller-checked; narrows the type
    const x = Math.round(finalRect.x);
    const y = Math.round(finalRect.y);
    const newAnchor = reoffsetWithinCell(current, orig.x - current.dx, orig.y - current.dy, x, y);
    session.batch(() => {
      if (!anchorsEqual(newAnchor, current)) {
        session.setElementAnchor(element.id, newAnchor);
      }
      const patch: ElementGeometryPatch = {};
      if (finalRect.w !== orig.w) patch.w = Math.round(finalRect.w);
      if (finalRect.h !== orig.h) patch.h = Math.round(finalRect.h);
      if (Object.keys(patch).length > 0) session.setElementRect(element.id, patch);
      // A drawing (non-confining) is cell-anchored only to FOLLOW its cell and
      // must never size it, so the fit is skipped.
      if (elementConfinesToCell(element.type)) {
        session.fitCell(newAnchor.rowId, newAnchor.colId);
      }
    });
  };

  /* ---------------- F-38 ports --------------------------------------- */

  /**
   * Arm the port for the edge the cursor is running along (F-38: "when the
   * cursor is around the edge of each side, the starting point shows"). The
   * band is a SCREEN distance so the affordance feels identical at any zoom.
   * Deliberately silent while a connector drag is in flight — the ports are
   * then all shown as targets and the arming would fight the snap highlight.
   */
  const armPort = (event: React.PointerEvent) => {
    if (!canConnect || connectorUi.getState().draft !== null) return;
    const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const point = {
      x: rect.x + (event.clientX - bounds.left) / zoom,
      y: rect.y + (event.clientY - bounds.top) / zoom,
    };
    const side = nearestSide(rect, point, PORT_HOVER_SCREEN_PX / zoom);
    connectorUi.getState().setHover(side === null ? null : { elementId: element.id, side });
  };

  const beginConnector = (event: React.PointerEvent, side: ConnectorSide) => {
    if (!canConnect || event.button !== 0) return;
    // Never the element's own drag, never a canvas pan: this press draws a line.
    event.stopPropagation();
    event.preventDefault();
    connectorUi
      .getState()
      .beginDraft({ elementId: element.id, side }, portPoint(rect, side), event.pointerId);
  };

  // F-46/C7: the claim must not outlive the interaction. If this view
  // unmounts MID-DRAG (the element was deleted remotely — pointer capture
  // dies with the DOM node, so endDrag/pointercancel never fire), clear the
  // awareness claim; otherwise a live, connected client would advertise a
  // phantom soft lock (and suppress everyone's drags if the delete is
  // undone) until it grabbed something else or disconnected.
  useEffect(() => {
    return () => {
      if (longPressRef.current !== null) {
        window.clearTimeout(longPressRef.current);
        longPressRef.current = null;
      }
      if (dragRef.current !== null) {
        const wasMoved = dragRef.current.moved;
        dragRef.current = null;
        session.setGrabbedElement(undefined);
        elementsUi.getState().clearLive();
        // Same mid-drag-unmount reasoning for the F-27 toolbar: a stale
        // "dragging" flag would hide the pill forever.
        if (wasMoved) onDragActive?.(false);
      }
    };
  }, [session, onDragActive, elementsUi]);

  // F-31: a plain left press on a card body arms the link-open intent, which
  // the double-click below spends. `beginDrag` selects the card and may start
  // a drag — a drag clears the intent (onPointerMove), so a gesture that moved
  // the card can never end in an open.
  const onWrapperPointerDown = (event: React.PointerEvent) => {
    // F-34: a modifier click is a SELECTION gesture, not a drag — it toggles
    // this element in or out of the multi-selection and stops there.
    if (event.button === 0 && (event.metaKey || event.ctrlKey || event.shiftKey)) {
      event.stopPropagation();
      event.preventDefault();
      if (!frozen) onSelect(element.id, true);
      return;
    }
    if (event.button === 0) {
      openIntentRef.current = element.type === "link-card";
    }
    beginDrag(event, "move");
  };

  /**
   * F-31 (amended 2026-08-30): a DOUBLE-click — double-tap on touch — opens the
   * card's URL in the OS default browser (see linkCard/open.ts for what that
   * means in each host). A single click now only selects.
   *
   * That split is the point of the amendment: while one click opened the card,
   * there was no way to select one — and so no way to reach its toolbar, its
   * handles or its context menu — without a browser tab appearing over the
   * board. Selecting is the common act; leaving the app is the rare one, so
   * the rare one is the deliberate gesture, and the toolbar's "Open in
   * browser" button (linkCard/toolbar.tsx) is its one-click equivalent.
   *
   * Not gated on the F-45 freeze or an F-46 peer grab: those gate WRITES, and
   * following a link is not one — the intent is armed in the handler above,
   * before the `beginDrag` that bails out in those cases. (The TOUCH pair does
   * ride `endDrag`, so a freeze does suppress it; a frozen board is temporary
   * and hides its toolbar too, which is not worth a second tap path.)
   */
  const openLink = () => {
    const intent = openIntentRef.current;
    openIntentRef.current = false;
    if (!intent) return;
    const url = linkCardUrl(element);
    if (url !== null) openLinkInBrowser(url);
  };

  // Suppress the stale-cache paint (see `cellUnresolvable`) unless a live
  // preview / remote grab is driving this element right now. All hooks above
  // run first, so this conditional return keeps the Rules of Hooks.
  if (cellUnresolvable && localRect === null && remoteGrab === undefined) return null;

  const Renderer = elementRenderers[element.type];

  // Handles/badges/outlines counter-scale so they stay usable at any zoom.
  const inv = 1 / zoom;
  // A drawing's bounding box is mostly transparent and routinely overlaps grid
  // cells — the box must not swallow their clicks. Its wrapper is pointer-inert;
  // the renderer re-enables events exactly along the ink (which bubble back up
  // here), and the handles/badge below re-enable themselves via CSS.
  const inkOnlyHitTest = element.type === "drawing";
  // Ports show while the cursor is around this element's edges, and on EVERY
  // connectable element while a line is being dragged (F-38: "other items show
  // where the other end can connect to").
  const showPorts = canConnect && (armedSide !== null || draftActive);
  // Corner handles are a single-element affordance: a group resize is not part
  // of the model (F-34 lists it separately), and four handles per element would
  // read as noise over a marquee selection.
  const showHandles = selected && canInteract && selectedIds.length === 1;
  return (
    <div
      className={`board-element${selected ? " selected" : ""}${remoteGrab ? " remote-grabbed" : ""}`}
      style={{
        left: rect.x,
        top: rect.y,
        width: Math.max(1, rect.w),
        height: Math.max(1, rect.h),
        ...(inkOnlyHitTest ? { pointerEvents: "none" as const } : undefined),
        ...(selected && !remoteGrab ? { outlineWidth: 2 * inv } : undefined),
        ...(remoteGrab
          ? {
              outline: `${2 * inv}px solid ${remoteGrab.user.color}`,
              boxShadow: `0 0 0 ${4 * inv}px ${withAlpha(remoteGrab.user.color, 0.25)}`,
              cursor: "not-allowed",
            }
          : undefined),
      }}
      onPointerDown={onWrapperPointerDown}
      onPointerMove={onWrapperPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => {
        if (dragRef.current === null) connectorUi.getState().setHover(null);
      }}
      onLostPointerCapture={onLostPointerCapture}
      onDoubleClick={openLink}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selected) onSelect(element.id, false);
        if (!frozen) onOpenMenu(element, e.clientX, e.clientY);
      }}
    >
      {Renderer !== undefined ? <Renderer element={element} /> : null}
      {remoteGrab !== undefined && (
        <div
          className="element-grab-badge"
          style={{
            background: remoteGrab.user.color,
            transform: `scale(${inv})`,
            transformOrigin: "left bottom",
          }}
        >
          {remoteGrab.user.name}
        </div>
      )}
      {showPorts &&
        CONNECTOR_SIDES.map((side) => (
          <div
            key={side}
            className={`element-port element-port-${side}${
              side === snapSide ? " snapped" : side === armedSide ? " armed" : ""
            }`}
            style={{
              width: PORT_SCREEN_PX * inv,
              height: PORT_SCREEN_PX * inv,
              borderWidth: 2 * inv,
            }}
            onPointerDown={(e) => beginConnector(e, side)}
          />
        ))}
      {showHandles && (
        <>
          {CORNERS.map((corner) => (
            <div
              key={corner}
              className={`element-handle element-handle-${corner}`}
              style={{
                width: HANDLE_SCREEN_PX * inv,
                height: HANDLE_SCREEN_PX * inv,
                borderWidth: 2 * inv,
                ...(corner.includes("w")
                  ? { left: -HANDLE_OFFSET_SCREEN_PX * inv }
                  : { right: -HANDLE_OFFSET_SCREEN_PX * inv }),
                ...(corner.includes("n")
                  ? { top: -HANDLE_OFFSET_SCREEN_PX * inv }
                  : { bottom: -HANDLE_OFFSET_SCREEN_PX * inv }),
              }}
              onPointerDown={(e) => beginDrag(e, corner)}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onLostPointerCapture={onLostPointerCapture}
            />
          ))}
        </>
      )}
    </div>
  );
}
